// metrics.js — die Rechenschicht des Tracking-Moduls.
//
// Grundregel: Berechnen statt behaupten. Jede Kennzahl wird aus den echten
// Datenquellen berechnet (Termine, HKPs, Recalls, Patienten, Bewertungen,
// importierte Umsatzstatistik) — egal ob die Quelle per Demo-Seed, CSV-Import
// oder Handeingabe gefüllt wurde. Fehlt die Quelle, liefert die Kennzahl
// { ok:false } und die UI zeigt einen ehrlichen Leerzustand — niemals
// stillschweigend erfundene Zahlen.
import { getBehandler } from "./settingsStore";
import { loadPatientsCache } from "./patientStore";
import { loadHkpsCache } from "./hkpStore";
import { loadRecallsCache } from "./recallStore";
import { scoreFactors, scoreMeta } from "../TrackingScreen";

const loadJson = (key, fallback) => { try { const s = localStorage.getItem(key); if (s) return JSON.parse(s); } catch {} return fallback; };

export const loadApts    = () => loadJson("dida_apts", []);
export const loadHkps    = loadHkpsCache;
export const loadRecalls = loadRecallsCache;
export const loadReviews = () => loadJson("dida_reviews", []);
const loadDaycfg = () => loadJson("dida_daycfg", null);

// "TT.MM.JJ" → Date
const deDate = (s) => {
  try { const [d, m, y] = s.split("."); const dt = new Date(2000 + +y, +m - 1, +d); return isNaN(dt) ? null : dt; } catch { return null; }
};
const isoToday = () => new Date().toISOString().slice(0, 10);

const fmtEur = (n) => "€ " + Math.round(n).toLocaleString("de-DE");
const pct = (n) => `${Math.round(n)} %`;

// ── HKP: Quote, Volumen, Entscheidungsdauer ───────────────────────────────
export function hkpMetrics(hkps) {
  if (!hkps.length) return { ok: false, hint: "Noch keine HKPs angelegt — lege im HKP-Manager den ersten an." };
  const IM_UMLAUF = ["vorgelegt", "eingereicht", "genehmigt"];
  // "durchgeführt" ist die Stufe nach "angenommen" — zählt für die
  // Akzeptanzquote weiterhin als angenommen.
  const ACCEPTED = ["angenommen", "durchgefuehrt"];
  const entschieden = hkps.filter(h => [...ACCEPTED, "abgelehnt"].includes(h.status));
  const angenommen = hkps.filter(h => ACCEPTED.includes(h.status)).length;
  const quote = entschieden.length ? Math.round(angenommen / entschieden.length * 100) : null;
  const offenVol = hkps.filter(h => IM_UMLAUF.includes(h.status)).reduce((s, h) => s + (h.wert || 0), 0);
  const imUmlauf = hkps.filter(h => IM_UMLAUF.includes(h.status)).length;
  // Wertbasierte Quote (für den Potenzial-Rechner): angenommenes Volumen ÷ entschiedenes Volumen
  const entschVol = entschieden.reduce((s, h) => s + (h.wert || 0), 0);
  const acceptedVol = hkps.filter(h => ACCEPTED.includes(h.status)).reduce((s, h) => s + (h.wert || 0), 0);
  // Umsetzung: wie viele angenommene Pläne wurden wirklich durchgeführt?
  const durchgefuehrt = hkps.filter(h => h.status === "durchgefuehrt").length;
  const umsetzungsquote = angenommen ? Math.round(durchgefuehrt / angenommen * 100) : null;
  const realisiertVol = hkps.filter(h => h.status === "durchgefuehrt").reduce((s, h) => s + (h.wert || 0), 0);
  // Ø Tage von "Patient vorgelegt" bis Patientenentscheidung
  const spans = hkps
    .filter(h => h.vorgelegt && h.entschieden)
    .map(h => { const a = deDate(h.vorgelegt), b = deDate(h.entschieden); return a && b ? (b - a) / 86400000 : null; })
    .filter(x => x !== null && x >= 0);
  const avgDecisionDays = spans.length ? Math.round(spans.reduce((s, x) => s + x, 0) / spans.length * 10) / 10 : null;
  return { ok: true, quote, entschieden: entschieden.length, angenommen, offenVol, imUmlauf, avgDecisionDays, total: hkps.length,
    entschVol, acceptedVol, durchgefuehrt, umsetzungsquote, realisiertVol };
}

// ── Umsatz aus importierter Statistik ─────────────────────────────────────
export function umsatzMetrics(statsRows) {
  const rows = (statsRows || []).filter(r => r.typ === "umsatz" && r.payload?.umsatz != null);
  if (!rows.length) return { ok: false, hint: "Noch keine Umsatzstatistik importiert — PVS-Import → Umsatzstatistik." };
  const trend = rows.map(r => ({ periode: r.periode, umsatz: r.payload.umsatz, neupatienten: r.payload.neupatienten, privatanteil: r.payload.privatanteil }));
  const latest = trend[trend.length - 1];
  const prev = trend.length > 1 ? trend[trend.length - 2] : null;
  const deltaPct = prev && prev.umsatz ? Math.round((latest.umsatz - prev.umsatz) / prev.umsatz * 100) : null;
  return { ok: true, trend, latest, prev, deltaPct, fmt: fmtEur(latest.umsatz) };
}

// ── Leistungsspektrum aus importierten BEMA/GOZ-Ziffern ───────────────────
// Beantwortet: Womit verdient die Praxis ihr Geld — und wo steckt Potenzial,
// das im reinen Monatsumsatz untergeht?
export function leistungenMetrics(statsRows) {
  const rows = (statsRows || []).filter(r => r.typ === "leistungen" && r.payload?.ziffer);
  if (!rows.length) return { ok: false, hint: "Noch keine Leistungsstatistik importiert — PVS-Import → Datei mit BEMA/GOZ-Ziffern (Ziffer, Anzahl, Honorar)." };

  const perioden = [...new Set(rows.map(r => r.payload.monat).filter(Boolean))].sort();
  // Je Ziffer aufsummieren (über Perioden und Behandler hinweg)
  const proZiffer = new Map();
  rows.forEach(({ payload: p }) => {
    const key = `${p.katalog || ""}|${p.ziffer}`;
    const acc = proZiffer.get(key) || { ziffer: p.ziffer, katalog: p.katalog || "", bezeichnung: "", anzahl: 0, betrag: 0, hatBetrag: false };
    acc.anzahl += p.anzahl || 0;
    if (p.betrag != null) { acc.betrag += p.betrag; acc.hatBetrag = true; }
    if (!acc.bezeichnung && p.bezeichnung) acc.bezeichnung = p.bezeichnung;
    proZiffer.set(key, acc);
  });
  const ziffern = [...proZiffer.values()];
  const gesamtAnzahl = ziffern.reduce((s, z) => s + z.anzahl, 0);
  const gesamtBetrag = ziffern.reduce((s, z) => s + z.betrag, 0);
  const mitBetrag = ziffern.some(z => z.hatBetrag);

  // Honorar je Katalog — der Privatanteil am Umsatz, aus echten Ziffern statt
  // aus dem Versichertenstatus geschätzt.
  const katalog = ["BEMA", "GOZ"].map(k => {
    const list = ziffern.filter(z => z.katalog === k);
    return {
      katalog: k,
      anzahl: list.reduce((s, z) => s + z.anzahl, 0),
      betrag: list.reduce((s, z) => s + z.betrag, 0),
    };
  }).filter(k => k.anzahl > 0);
  const gozAnteil = mitBetrag && gesamtBetrag > 0
    ? Math.round((katalog.find(k => k.katalog === "GOZ")?.betrag || 0) / gesamtBetrag * 100)
    : null;

  const sortiert = (feld) => [...ziffern].sort((a, b) => b[feld] - a[feld]);
  return {
    ok: true,
    ziffern, gesamtAnzahl, gesamtBetrag, mitBetrag, katalog, gozAnteil,
    topAnzahl: sortiert("anzahl").slice(0, 10),
    topBetrag: mitBetrag ? sortiert("betrag").slice(0, 10) : [],
    zifferCount: ziffern.length,
    perioden,
    // Ø Honorar je Abrechnung — zeigt, ob viele kleine oder wenige große
    // Leistungen den Umsatz tragen.
    schnitt: mitBetrag && gesamtAnzahl > 0 ? gesamtBetrag / gesamtAnzahl : null,
  };
}

// ── Kassen-/Privatmix aus Patientenstamm (+ Umsatzanteil aus Import) ──────
export function kassenmixMetrics(patients, umsatz) {
  if (!patients.length) return { ok: false, hint: "Noch keine Patienten vorhanden." };
  const byKv = {};
  patients.forEach(p => { const kv = (p.kv || "Sonstige").trim() || "Sonstige"; byKv[kv] = (byKv[kv] || 0) + 1; });
  const verteilung = Object.entries(byKv).map(([kv, count]) => ({ kv, count, pct: Math.round(count / patients.length * 100) })).sort((a, b) => b.count - a.count);
  const privatPct = Math.round((byKv["Privat"] || 0) / patients.length * 100);
  const umsatzPrivat = umsatz?.ok && umsatz.latest?.privatanteil != null ? umsatz.latest.privatanteil : null;
  return { ok: true, verteilung, privatPct, umsatzPrivat };
}

// ── Ø Umsatz je Patient (aus Import ÷ aktive Patienten) ──────────────────
export function umsatzProPatient(patients, umsatz) {
  if (!umsatz?.ok) return { ok: false, hint: "Braucht importierte Umsatzstatistik (PVS-Import)." };
  const aktive = patients.filter(p => p.status !== "Inaktiv").length || patients.length;
  if (!aktive) return { ok: false, hint: "Noch keine Patienten vorhanden." };
  return { ok: true, value: umsatz.latest.umsatz / aktive, periode: umsatz.latest.periode, aktive, fmt: fmtEur(umsatz.latest.umsatz / aktive) };
}

// ── Ausfallquote: abgesagte Termine der letzten 30 Tage ───────────────────
export function ausfallMetrics(apts) {
  const today = isoToday();
  const cutoff = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const past = apts.filter(a => a.date && a.date <= today && a.date >= cutoff);
  if (!past.length) return { ok: false, hint: "Noch keine vergangenen Termine in den letzten 30 Tagen." };
  const abgesagt = past.filter(a => a.status === "abgesagt").length;
  return { ok: true, quote: Math.round(abgesagt / past.length * 100), abgesagt, gesamt: past.length, fmt: pct(abgesagt / past.length * 100) };
}

// ── Recall: Fällige + Rücklauf (kontaktiert → gebucht) ────────────────────
export function recallMetrics(recalls) {
  if (!recalls.length) return { ok: false, hint: "Noch keine Recall-Liste — per PVS-Import einspielen oder im Recall-Tab pflegen." };
  const offen = recalls.filter(r => r.status === "offen").length;
  const ueberfaellig = recalls.filter(r => r.status === "offen" && (r.tage || 0) > 60).length;
  const kontaktiert = recalls.filter(r => r.status === "kontaktiert").length;
  const gebucht = recalls.filter(r => r.status === "gebucht").length;
  const ruecklauf = (kontaktiert + gebucht) ? Math.round(gebucht / (kontaktiert + gebucht) * 100) : null;
  return { ok: true, offen, ueberfaellig, kontaktiert, gebucht, ruecklauf, total: recalls.length };
}

// ── Scorecard-Aggregat ────────────────────────────────────────────────────
export function scoreMetrics(patients) {
  if (!patients.length) return { ok: false, hint: "Noch keine Patienten vorhanden." };
  const scores = patients.map(p => scoreFactors(p).score);
  const avg = Math.round(scores.reduce((s, x) => s + x, 0) / scores.length);
  const counts = { A: 0, B: 0, C: 0, D: 0 };
  scores.forEach(s => { counts[scoreMeta(s).grade]++; });
  return { ok: true, avg, counts, patientCount: patients.length };
}

// ── Auslastung: gebuchte Minuten ÷ Kapazität (aktuelle Woche) ─────────────
export function auslastungMetrics(apts) {
  const now = new Date();
  const monday = new Date(now); monday.setDate(now.getDate() - ((now.getDay() + 6) % 7)); monday.setHours(0, 0, 0, 0);
  const friday = new Date(monday); friday.setDate(monday.getDate() + 4);
  const iso = (d) => d.toISOString().slice(0, 10);
  const week = apts.filter(a => a.date >= iso(monday) && a.date <= iso(friday) && a.status !== "abgesagt");
  if (!week.length) return { ok: false, hint: "Keine Termine in der aktuellen Woche — Terminplan füllen oder Kalender-Abo einrichten." };
  const daycfg = loadDaycfg();
  let kapProTag = 0;
  for (let wd = 1; wd <= 5; wd++) {
    const cfg = daycfg?.[wd];
    if (cfg?.closed) continue;
    kapProTag += ((cfg?.end ?? 18) - (cfg?.start ?? 8)) * 60;
  }
  const BEHANDLER = getBehandler();
  if (!BEHANDLER.length) return { ok: false, hint: "Noch keine Behandler angelegt — Benutzermenü → Praxis & Behandler." };
  const kapMin = kapProTag * BEHANDLER.length;
  const beh = BEHANDLER.map(b => {
    const mins = week.filter(a => a.behandler === b.id).reduce((s, a) => s + (a.dur || 30), 0);
    return { id: b.id, name: b.name, mins, pct: kapMin ? Math.round(mins / (kapProTag || 1) * 100) : 0 };
  });
  const gebuchtMin = beh.reduce((s, b) => s + b.mins, 0);
  return { ok: true, pct: kapMin ? Math.round(gebuchtMin / kapMin * 100) : 0, gebuchtMin, kapMin, beh, termine: week.length };
}

// ── Feedback aus Bewertungen ──────────────────────────────────────────────
export function feedbackMetrics(reviews) {
  if (!reviews.length) return { ok: false, hint: "Noch keine Bewertungen — Umfrage-Link im Tab »Bewertungen & Umfrage« erzeugen und an Patienten geben." };
  const avg = reviews.reduce((s, r) => s + (r.stars || 0), 0) / reviews.length;
  const dist = [5, 4, 3, 2, 1].map(stars => ({ stars, count: reviews.filter(r => r.stars === stars).length }));
  return { ok: true, avg: Math.round(avg * 10) / 10, count: reviews.length, dist };
}

// ── Churn: Patienten, die der Praxis verloren gehen ───────────────────────
// Risiko = letzter Besuch länger als 12 Monate her UND kein zukünftiger
// Termin. Rechnet über den gesamten Patientenstamm — nicht nur die
// PZR-Recall-Liste.
export function churnMetrics(patients, apts) {
  if (!patients.length) return { ok: false, hint: "Noch keine Patienten vorhanden — per PVS-Import einspielen." };
  const today = isoToday();
  const futureNames = new Set(
    apts.filter(a => a.date && a.date >= today && a.status !== "abgesagt")
        .map(a => (a.patient || "").toLowerCase().trim())
  );
  const hasFuture = (p) =>
    futureNames.has(`${p.vorname} ${p.nachname}`.toLowerCase()) ||
    futureNames.has(`${p.nachname}, ${p.vorname}`.toLowerCase());
  const withDays = patients.map(p => {
    const d = deDate(p.last || "");
    return { p, days: d ? Math.round((Date.now() - d.getTime()) / 86400000) : null };
  });
  const known = withDays.filter(x => x.days != null && x.days >= 0);
  if (!known.length) return { ok: false, hint: "Churn braucht das Feld „Letzter Termin“ aus dem Patienten-Import." };
  const risk = known
    .filter(x => x.days > 365 && !hasFuture(x.p))
    .sort((a, b) => b.days - a.days);
  const pctRisk = Math.round(risk.length / known.length * 100);
  return { ok: true, risk: risk.length, pct: pctRisk, basis: known.length,
    list: risk.slice(0, 30).map(x => ({ name: `${x.p.nachname}, ${x.p.vorname}`, last: x.p.last, monate: Math.round(x.days / 30), status: x.p.status || "" })) };
}

// ── Altersstruktur des Patientenstamms ────────────────────────────────────
// Quelle ist das Geburtsdatum aus dem Patienten-Import. Zweistellige
// Jahreszahlen werden bewusst NICHT geraten ("14" wäre 1914 oder 2014) —
// solche Patienten zählen als "ohne Geburtsdatum" statt als Falschangabe.
const birthDate = (s) => {
  const v = String(s || "").trim();
  let y, m, d;
  let hit = v.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);        // TT.MM.JJJJ
  if (hit) { [, d, m, y] = hit; }
  else {
    hit = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);            // JJJJ-MM-TT (ISO)
    if (hit) { [, y, m, d] = hit; } else return null;
  }
  const dt = new Date(+y, +m - 1, +d);
  return isNaN(dt) || dt > new Date() ? null : dt;
};

// Alter in vollen Jahren zum heutigen Tag
const ageFrom = (dt) => {
  const now = new Date();
  let a = now.getFullYear() - dt.getFullYear();
  const beforeBirthday = now.getMonth() < dt.getMonth() ||
    (now.getMonth() === dt.getMonth() && now.getDate() < dt.getDate());
  if (beforeBirthday) a--;
  return a;
};

// Gruppen mit zahnmedizinischem Bezug: Kinder (KFO/Kinderprophylaxe),
// junge Erwachsene, mittleres Alter, Vorruhestand, Senioren (Zahnersatz).
export const AGE_GROUPS = [
  { key: "0-17",  label: "0–17 Jahre",  min: 0,  max: 17 },
  { key: "18-34", label: "18–34 Jahre", min: 18, max: 34 },
  { key: "35-49", label: "35–49 Jahre", min: 35, max: 49 },
  { key: "50-64", label: "50–64 Jahre", min: 50, max: 64 },
  { key: "65+",   label: "65+ Jahre",   min: 65, max: 200 },
];

export function demografieMetrics(patients) {
  if (!patients.length) return { ok: false, hint: "Noch keine Patienten vorhanden — per PVS-Import einspielen." };
  const ages = [];
  let ohneDob = 0;
  patients.forEach(p => {
    const dt = birthDate(p.dob);
    const a = dt ? ageFrom(dt) : null;
    if (a == null || a < 0 || a > 120) ohneDob++; else ages.push(a);
  });
  if (!ages.length) return { ok: false, hint: `Für keinen der ${patients.length} Patienten liegt ein auswertbares Geburtsdatum vor — das Feld „Geburtsdatum“ (TT.MM.JJJJ) beim Patienten-Import mitgeben.` };
  const basis = ages.length;
  const groups = AGE_GROUPS.map(g => {
    const count = ages.filter(a => a >= g.min && a <= g.max).length;
    return { key: g.key, label: g.label, count, pct: Math.round(count / basis * 100) };
  });
  const avg = Math.round(ages.reduce((s, a) => s + a, 0) / basis);
  const sortedAges = [...ages].sort((a, b) => a - b);
  const median = sortedAges.length % 2
    ? sortedAges[(sortedAges.length - 1) / 2]
    : Math.round((sortedAges[sortedAges.length / 2 - 1] + sortedAges[sortedAges.length / 2]) / 2);
  const groesste = [...groups].sort((a, b) => b.count - a.count)[0];
  const kinderPct = groups[0].pct;
  const seniorPct = groups[4].pct;
  return { ok: true, groups, avg, median, basis, ohneDob, total: patients.length, groesste, kinderPct, seniorPct };
}

// ── Noch nicht erfassbare Kennzahlen: ehrliche Hinweise ───────────────────
export const NOT_YET = {
  wartezeit:  "Die Wartezeit auf einen Termin braucht den Buchungszeitpunkt je Termin — wird ab jetzt bei neuen dida-Buchungen erfasst werden (folgt).",
  wartezimmer:"Die Wartezimmer-Zeit entsteht aus der Check-in-Erfassung im Terminplan — noch nicht aggregiert.",
  dauer:      "Ist-Behandlungsdauern brauchen eine Ende-Erfassung am Stuhl — aktuell wird nur die Plandauer gespeichert.",
  igel:       "Die IGeL-Konversionsrate braucht die Erfassung von Angeboten und Abschlüssen — Quelle folgt mit dem Abrechnungs-Slice.",
};

// ── Alles zusammen ────────────────────────────────────────────────────────
export function computeMetrics(statsRows) {
  const patients = loadPatientsCache();
  const apts = loadApts();
  const hkps = loadHkps();
  const recalls = loadRecalls();
  const reviews = loadReviews();
  const umsatz = umsatzMetrics(statsRows);
  return {
    hkp: hkpMetrics(hkps),
    umsatz,
    leistungen: leistungenMetrics(statsRows),
    kassenmix: kassenmixMetrics(patients, umsatz),
    umsProPatient: umsatzProPatient(patients, umsatz),
    ausfall: ausfallMetrics(apts),
    recall: recallMetrics(recalls),
    churn: churnMetrics(patients, apts),
    demografie: demografieMetrics(patients),
    score: scoreMetrics(patients),
    auslastung: auslastungMetrics(apts),
    feedback: feedbackMetrics(reviews),
  };
}
